import React, { ReactNode } from 'react'

import { Button, Group, Stack, Text } from '@mantine/core'
import { useElementSize } from '@mantine/hooks'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { Link } from 'wouter'

import { Toast } from '#components/Toast'
import { useMst } from '#hooks'
import { SnapshotSummary } from '#util/ScriptoApiClient/types'

import { isSnapshotSelectable, isSnapshotSelected } from './helpers'
import { SelectionMode, SnapshotCard } from './SnapshotCard'
import { SnapshotFilter } from './SnapshotFilter'
import {
  SnapshotListEntries,
  SnapshotListEntriesSkeleton,
} from './SnapshotListEntries'
import { StickySnapshotCard } from './StickySnapshotCard'
import { type SnapshotViewType } from './types'
import { useSelectedSnapshots } from './useSnapshotLandData'

import styles from './SnapshotList.module.css'

// if s1 (right side/bottom) is older chronologically
// or the same version but older temporally, swap them
const shouldSwapSnapshots = (
  s1: SnapshotSummary | null | undefined,
  s2: SnapshotSummary | null | undefined,
): boolean => {
  if (!s1 || !s2) return false
  // a higher version number is indicative of a more recent snapshot
  if (s1.version > s2.version) return false
  if (s1.version < s2.version) return true
  // two snapshots can share the same version if one is an autosave and the other isnt
  return s1.createdAt.valueOf() < s2.createdAt.valueOf()
}

// if the history list is narrow, we show the cards top/bottom
const SIDE_BY_SIDE_STICKY_THRESHOLD = 600

const StickyCards = ({
  mode,
  scriptId,
  width,
}: {
  scriptId: string
  mode: SelectionMode
  width: number
}) => {
  const wrapped = width < SIDE_BY_SIDE_STICKY_THRESHOLD
  return (
    <div
      className={cn(styles.selected, {
        [styles.wrapped]: wrapped,
      })}
    >
      {mode === 'comparison' && (
        <StickySnapshotCard
          wrapped={wrapped}
          position="top"
          scriptId={scriptId}
        />
      )}
      <StickySnapshotCard
        wrapped={wrapped}
        position="bottom"
        scriptId={scriptId}
      />
    </div>
  )
}

const HEADERS: Record<SnapshotViewType, string> = {
  static: 'Snapshots',
  asterisk: 'Snapshot comparison',
  diff: 'Snapshot comparison',
}

const SnapshotListHeader = (
  props: React.PropsWithChildren<{ scriptId: string }>,
) => (
  <Group
    wrap="nowrap"
    justify="space-between"
    align="center"
    className={styles.header}
    gap={5}
    pb={10}
    px={10}
    pt={10}
  >
    <Text lineClamp={1} style={{ wordBreak: 'break-all' }} fw="bold">
      {props.children}
    </Text>
    <SnapshotFilter scriptId={props.scriptId} />
  </Group>
)

export const SnapshotList = observer(function SnapshotList({
  scriptId,
}: {
  scriptId: string
}) {
  const {
    snap1,
    snap2,
    currentView,
    selectSnapshotUrl,
    swapSnapshots,
    dismissSnapshot,
    trackSnapshotEvent: trackEvent,
  } = useMst().view.snapshotLand
  const { ref: sizeRef, width } = useElementSize()

  const { topQuery, bottomQuery } = useSelectedSnapshots(scriptId)

  React.useEffect(() => {
    if (shouldSwapSnapshots(topQuery?.data, bottomQuery?.data)) {
      swapSnapshots()
    }
  }, [bottomQuery.data, topQuery.data, swapSnapshots, snap1, snap2])

  // if the same snapshot is selected twice, then dismiss the second
  // copy. This can happen when switching views
  React.useEffect(() => {
    if (snap1 && snap1 === snap2) {
      dismissSnapshot(2)
    }
  })

  return (
    <Stack className={styles.list} gap={0} ref={sizeRef}>
      <SidebarErrorBoundary>
        <SnapshotListHeader scriptId={scriptId}>
          {HEADERS[currentView]}
        </SnapshotListHeader>
        <StickyCards
          scriptId={scriptId}
          mode={currentView === 'static' ? 'solo' : 'comparison'}
          width={width}
        />
        <React.Suspense fallback={<SnapshotListEntriesSkeleton />}>
          <SnapshotListEntries includeCurrScript scriptId={scriptId}>
            {(snapshot) => {
              const opts = {
                snap1,
                snap2,
                currentView,
                snapshotId: snapshot.id,
              }
              const isSelected = isSnapshotSelected(opts)

              const snapshotCard = (
                <SnapshotCard
                  data={snapshot}
                  selected={isSelected}
                  editButton="hover"
                />
              )

              if (isSnapshotSelectable(opts)) {
                return (
                  <Link
                    href={selectSnapshotUrl(snapshot.id)}
                    onClick={() => trackEvent('SELECT_SNAPSHOT')}
                    asChild
                  >
                    {snapshotCard}
                  </Link>
                )
              }

              return snapshotCard
            }}
          </SnapshotListEntries>
        </React.Suspense>
      </SidebarErrorBoundary>
    </Stack>
  )
})

type ErrorBoundaryProps = React.PropsWithChildren<{
  renderFallback: (renderProps: {
    message: string
    resetErrorBoundary: () => void
  }) => ReactNode
  onReset: () => void
}>
type ErrorBoundaryState = { hasError: boolean; message: string }

// @TODO [+react-error-boundary] replace (has the same interface)
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, message: '' }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error.message }
  }

  render() {
    if (this.state.hasError) {
      return this.props.renderFallback({
        message: this.state.message,
        resetErrorBoundary: () => {
          this.props.onReset()
          this.setState({ hasError: false })
        },
      })
    }

    return this.props.children
  }
}

function SidebarErrorBoundary(props: React.PropsWithChildren) {
  return (
    <QueryErrorResetBoundary>
      {({ reset: resetReactQuery }) => (
        <ErrorBoundary
          onReset={resetReactQuery}
          renderFallback={({ resetErrorBoundary, message }) => (
            <Stack px={10}>
              <Toast
                message={message}
                icon="fas fa-circle-exclamation"
                type="error"
                dismissable={false}
              />

              <Button onClick={resetErrorBoundary}>Retry</Button>
            </Stack>
          )}
        >
          {props.children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}
